import { SfLog } from '@app/adapters/logger'
import SFetch from '@app/utils/sfetch'

import useOrderedRemarks from '@app/application/client_meetings/screens/prepare/composables/use-ordered-remarks'
import createMeetingPreparationStore from '@app/application/client_meetings/screens/prepare/stores/main'
import { REMARK_ACTIONS } from '@app/application/communication/constants'

// Adds the flash class for one animation cycle. Returns a cancel function
// used both for re-entrancy (back-to-back scroll events) and teardown
// (component unmounted before animationend fires).
function flashRowHighlight(row) {
  row.classList.add('report-row-highlight')
  const cleanup = () => row.classList.remove('report-row-highlight')
  row.addEventListener('animationend', cleanup, { once: true })
  return () => {
    row.removeEventListener('animationend', cleanup)
    cleanup()
  }
}

export default class ReportTable {
  constructor(container) {
    this.container = container
    this.dataLinePath = container.dataset.reportDataLinePath
    this.dataLineSelector = "[data-behaviour~='report-table-data-line']"
    this.dataLineOriginalValues = {}
    this.presentationMode = window.Silverfin.presentationMode

    if (this.presentationMode) {
      const headerName = document.getElementById('header-report-name')
      headerName.innerText = window.I18n.t('reports.report_details')
    }

    this.fetchDiscussionIndicators(
      container.dataset.reportTemplateId,
      container
    )

    this.eventListenerRemovers = []
    this.clearHighlight = null

    this.handleDataLineFocus = this.handleDataLineFocus.bind(this)
    this.handleDataLineChange = this.handleDataLineChange.bind(this)
    this.handleDataLineKeyUp = this.handleDataLineKeyUp.bind(this)
    this.handleDataLineSubmit = this.handleDataLineSubmit.bind(this)
    this.handleAccountClick = this.handleAccountClick.bind(this)
    this.handleElementDetailsClick = this.handleElementDetailsClick.bind(this)
    this.handleRefreshCommunicationData =
      this.handleRefreshCommunicationData.bind(this)
    this.handleScrollToReportElement =
      this.handleScrollToReportElement.bind(this)

    this.registerListeners()
  }

  async fetchDiscussionIndicators(
    reportTemplateId,
    container,
    { fresh = false } = {}
  ) {
    let data

    if (this.presentationMode) {
      const store = createMeetingPreparationStore({
        firmId: Silverfin.currentFirmId,
        companyId: Silverfin.currentCompanyId,
      })

      await store.dispatch('fetchCommunication')

      const { accountRemarks, elementRemarks } = useOrderedRemarks(store)
      data = {
        accounts: accountRemarks.value,
        elements: elementRemarks.value,
      }
    } else {
      const url = Silverfin.Routes.discussion_indicators_report_path(
        Silverfin.currentFirmId,
        Silverfin.currentCompanyId,
        reportTemplateId,
        fresh ? { fresh: 1 } : {}
      )

      try {
        data = await SFetch.json(url)
      } catch (e) {
        SfLog.warn('Failed to fetch discussion indicators', {
          extra: {
            error: e,
          },
        })

        return
      }
    }

    this.clearDiscussionIndicators(container)

    const { accounts = {}, elements = {} } = data || {}

    const applyIndicators = (map, buildSelector) => {
      for (const ledgerId in map) {
        for (const id of map[ledgerId]) {
          const link = container.querySelector(buildSelector(ledgerId, id))
          if (link) {
            this.addDiscussionIndicator(link.closest('td'))
          }
        }
      }
    }

    applyIndicators(
      accounts,
      (ledgerId, accountId) =>
        `a[data-ledger-id="${ledgerId}"][data-account-id="${accountId}"]`
    )
    applyIndicators(
      elements,
      (ledgerId, elementId) =>
        `a[data-ledger-id="${ledgerId}"][data-report-element-id="${elementId}"]`
    )
  }

  clearDiscussionIndicators(container) {
    const cells = container.querySelectorAll('.cell-with-discussion-icon')

    cells.forEach((cell) => {
      cell.classList.remove(
        'cell-with-discussion-icon',
        'tw-relative',
        'tw-pl-6'
      )

      const icon = cell.querySelector(
        ':scope > i.fa-comment, :scope > i.fa-user-group'
      )
      if (icon) icon.remove()

      const discussionLink = cell.querySelector('a')
      if (discussionLink) {
        delete discussionLink.dataset.testid
      }
    })
  }

  addDiscussionIndicator(cell) {
    const discussionLink = cell.querySelector('a')

    if (discussionLink) {
      if (cell.classList.contains('cell-with-discussion-icon')) {
        return
      }

      const icon = document.createElement('i')
      icon.classList.add(
        'tw-text-sf-neutral-light-1',
        'tw-text-xs',
        'tw-absolute',
        'tw-left-2',
        'tw-top-2'
      )
      if (this.presentationMode) {
        icon.classList.add('fa-solid', 'fa-user-group')
      } else {
        icon.classList.add('fa-solid', 'fa-comment')
      }

      cell.classList.add('cell-with-discussion-icon', 'tw-relative', 'tw-pl-6')
      discussionLink.dataset.testid = 'report-discussion-link'
      cell.prepend(icon)
    }
  }

  handleDataLineChange(event) {
    const input = event.target
    const id = input.dataset.dataLineId

    if (this.dataLineOriginalValues[id] !== event.target.value) {
      input.dataset.modified = true

      requestAnimationFrame(() => {
        input.classList.add('report-table-data-line-modified')
      })
    }
  }

  handleDataLineFocus(event) {
    const id = event.target.dataset.dataLineId

    if (!this.dataLineOriginalValues[id]) {
      this.dataLineOriginalValues[id] = event.target.value
    }
  }

  handleDataLineKeyUp(event) {
    if (event.key === 'Enter' || event.keyCode === 13 || event.which === 13) {
      this.handleDataLineSubmit()
    } else {
      this.handleDataLineChange(event)
    }
  }

  handleDataLineSubmit() {
    const dataToPost = {}

    const dataLines = this.container.querySelectorAll(
      `${this.dataLineSelector}[data-modified]`
    )

    dataLines.forEach((dataline) => {
      const dataLineId = dataline.dataset.dataLineId
      const dataLineDate = dataline.dataset.dataLineEndDate
      const dataLineValueType = dataline.dataset.dataLineValueType

      if (!dataToPost[dataLineId]) {
        dataToPost[dataLineId] = {}
      }

      if (!dataToPost[dataLineId][dataLineDate]) {
        dataToPost[dataLineId][dataLineDate] = {}
      }

      dataToPost[dataLineId][dataLineDate][dataLineValueType] = dataline.value
    })

    Silverfin.Spinner.start()

    $.post(this.dataLinePath, {
      report_data_line_entries: dataToPost,
    }).fail(Silverfin.Spinner.failed())
  }

  handleRefreshCommunicationData({ action } = {}) {
    if (
      action === REMARK_ACTIONS.CREATED ||
      action === REMARK_ACTIONS.DESTROYED
    ) {
      this.fetchDiscussionIndicators(
        this.container.dataset.reportTemplateId,
        this.container,
        { fresh: true }
      )
    }
  }

  handleAccountClick(event) {
    event.preventDefault()

    const element = event.currentTarget
    const {
      reportId,
      accountId,
      analyticalDimensionId,
      relative,
      ledgerId,
      subtractLedgerId,
      value,
    } = element.dataset

    window.modalCitizen.openModalPortal('report-account-details-modal', {
      reportId: Number(reportId),
      accountId: Number(accountId),
      ledgerId: Number(ledgerId),
      subtractLedgerId: subtractLedgerId ? Number(subtractLedgerId) : undefined,
      relative,
      value,
      analyticalDimensionId: analyticalDimensionId
        ? Number(analyticalDimensionId)
        : undefined,
    })
  }

  handleElementDetailsClick(event) {
    event.preventDefault()
    event.stopPropagation()

    const {
      modalTitle,
      modalPeriod,
      reportElementId,
      ledgerId,
      reportId,
      value,
    } = event.currentTarget.dataset

    window.modalCitizen.openModalPortal('report-element-details-modal', {
      title: modalTitle,
      period: modalPeriod,
      reportElementId: Number(reportElementId),
      ledgerId: Number(ledgerId),
      reportId: Number(reportId),
      value: value || '',
    })
  }

  registerListeners() {
    const dataLines = this.container.querySelectorAll(this.dataLineSelector)

    dataLines.forEach((dataline) => {
      dataline.addEventListener('focus', this.handleDataLineFocus)
      dataline.addEventListener('keyup', this.handleDataLineKeyUp)

      this.eventListenerRemovers.push(() => {
        dataline.removeEventListener('focus', this.handleDataLineFocus)
        dataline.removeEventListener('keyup', this.handleDataLineKeyUp)
      })
    })

    const accounts = this.container.querySelectorAll(
      '[data-behaviour="account-open-modal"]'
    )

    accounts.forEach((account) => {
      account.onclick = null
      account.addEventListener('click', this.handleAccountClick)

      this.eventListenerRemovers.push(() => {
        account.removeEventListener('click', this.handleAccountClick)
      })
    })

    const elementDetailsLinks = this.container.querySelectorAll(
      '[data-behaviour="report-element-details-link"]'
    )

    elementDetailsLinks.forEach((link) => {
      link.addEventListener('click', this.handleElementDetailsClick)

      this.eventListenerRemovers.push(() => {
        link.removeEventListener('click', this.handleElementDetailsClick)
      })
    })

    window.eventHub.$on(
      'refreshCommunicationData',
      this.handleRefreshCommunicationData
    )
    this.eventListenerRemovers.push(() => {
      window.eventHub.$off(
        'refreshCommunicationData',
        this.handleRefreshCommunicationData
      )
    })

    window.eventHub.$on(
      'scrollToReportElement',
      this.handleScrollToReportElement
    )
    this.eventListenerRemovers.push(() => {
      window.eventHub.$off(
        'scrollToReportElement',
        this.handleScrollToReportElement
      )
    })
  }

  handleScrollToReportElement({ elementId }) {
    const row = this.container
      .querySelector(`[data-report-element-id="${elementId}"]`)
      ?.closest('tr')
    if (!row) return

    row.scrollIntoView({ behavior: 'smooth', block: 'center' })
    this.clearHighlight?.()
    this.clearHighlight = flashRowHighlight(row)
  }

  teardown() {
    this.container = undefined
    if (this.clearHighlight) this.clearHighlight()
    this.eventListenerRemovers.forEach((remover) => remover())
  }
}
